/**
 * GBTrack — unified tracking utility (core).
 *
 * 공개 API (window.GBTrack):
 *   - track(event, props?, options?)
 *   - register(adapter)
 *   - enable(adapterName) / disable(adapterName)
 *   - getContext()
 *
 * 어댑터 인터페이스:
 *   { name, init(config), isReady(), onReady(cb)?, transform(event, props, ctx), send(payload), isAllowed(event)?,
 *     onConsentRevoked(revoked)?, onConsentRestored(restored)? }
 *
 * FP-1152 동의 게이팅:
 *   - 모든 GBTrack 커스텀 이벤트는 analytics 카테고리다. analytics 동의 전에는
 *     전송하지 않고 **메모리 큐에만** 쌓았다가 동의 후 flush 한다 (스토리지 저장 금지).
 *   - 식별자(gb_did / gb_sid / gb_attr)도 analytics 동의 전에는 생성/저장하지 않는다.
 *   - 광고 클릭 ID(gclid·fbclid·ttclid)는 marketing 카테고리로 utm 과 분리해 저장한다.
 *   - 동의 철회 시 어댑터 정리 훅 호출 + 로컬 식별자 삭제.
 *   동의 조회는 snippets/consent-signal.liquid 의 window.GBConsent 만 사용한다.
 */
(function () {
  'use strict';

  var cfg = window.GBTrackConfig || {
    enabled: false,
    design_mode: false,
    adapters: {},
    liquid_context: {},
  };

  var adapters = {};          // name → adapter
  var disabledSet = {};       // 런타임 disable 플래그
  var globalQueueFlushed = false;

  var CONSENT_ANALYTICS = 'analytics';
  var CONSENT_MARKETING = 'marketing';

  // 동의 시그널 미로드 = fail-closed. consent-signal.liquid 가 <head> 에서 항상 먼저 렌더되지만,
  // 스니펫 누락 레이아웃에서 조용히 전송이 살아나는 것보다 조용히 멈추는 쪽이 안전하다.
  function consentAllows(category) {
    var c = window.GBConsent;
    if (!c || typeof c.isAllowed !== 'function') return false;
    return c.isAllowed(category);
  }

  // 동의 상태가 확정됐는지(= 이용자 선택 또는 지역 판정이 끝났는지).
  // 미확정과 "명시적 거부"는 다르게 다뤄야 한다 — 아래 track() 참고.
  function consentResolved() {
    var c = window.GBConsent;
    return !!(c && typeof c.isResolved === 'function' && c.isResolved());
  }

  /** @type {string | null} */
  var currentUserId = null; // 현재 페이지 세션의 회원 식별자 (null = 익명)
  /** @type {Record<string, string | null>} */
  var appliedUid = {};      // adapter.name → 마지막 적용 user_id (중복 적용 방지)

  function normalizeUserId(id) {
    if (id === null || id === undefined) return null;
    var s = String(id).trim();
    return s === '' ? null : s;
  }

  function applyIdentityTo(adapter) {
    var uid = currentUserId;
    if (uid == null) return;
    if (typeof adapter.setUserId !== 'function') return;
    if (appliedUid[adapter.name] === uid) return; // 이미 적용됨 → 중복 push 방지 (예: setUser 후 drain replay)
    try { adapter.setUserId(uid); appliedUid[adapter.name] = uid; }
    catch (err) { console.warn('[GBTrack:' + adapter.name + '] setUserId failed', err); }
  }

  var IS_DEV = /^(localhost|127\.0\.0\.1|\[?::1\]?)$/.test(location.hostname);

  // 무시하고 진행해도 되는 예외의 적재 버퍼.
  // 콘솔로 바로 흘리면 내부 구조가 노출되므로, 버퍼에만 쌓고 GBTrack.errors() 로만 조회한다.
  var ERROR_LOG_MAX = 50;
  var _errorBuffer = [];
  function logError(scope, error) {
    _errorBuffer.push({
      scope: scope,
      message: error && error.message ? error.message : String(error),
      at: new Date().toISOString(),
    });
    if (_errorBuffer.length > ERROR_LOG_MAX) _errorBuffer.shift();
  }

  // ?gbtrack=debug → sessionStorage에 플래그 세팅, 이후 새로고침까지 로그 강제 on
  var DEBUG_KEY = 'gb_debug';
  try {
    if (/[?&]gbtrack=debug(&|$)/.test(location.search)) {
      sessionStorage.setItem(DEBUG_KEY, '1');
    }
  } catch (e) {
    // sessionStorage 차단(프라이빗 모드 등) → 디버그 플래그 없이 계속 진행
    logError('tracking:debugFlag:write', e);
  }
  var IS_DEBUG = false;
  try {
    IS_DEBUG = sessionStorage.getItem(DEBUG_KEY) === '1';
  } catch (e) {
    logError('tracking:debugFlag:read', e);
  }

  var HISTORY_MAX = 50;
  var _historyBuffer = [];
  function recordHistory(entry) {
    if (!IS_DEBUG) return;
    _historyBuffer.push(entry);
    if (_historyBuffer.length > HISTORY_MAX) _historyBuffer.shift();
  }

  function uuid() {
    if (window.crypto && typeof window.crypto.randomUUID === 'function') {
      return window.crypto.randomUUID();
    }
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      var r = (Math.random() * 16) | 0;
      var v = c === 'x' ? r : (r & 0x3) | 0x8;
      return v.toString(16);
    });
  }

  var SESSION_KEY = 'gb_sid';
  var SESSION_TS_KEY = 'gb_sid_ts';
  var SESSION_IDLE_MS = 30 * 60 * 1000;
  var DEVICE_KEY = 'gb_did';

  // 광고 attribution 키. URL 진입 시 1회 캡처 후 세션 동안 유지 (페이지 이동에도 모든 이벤트에 첨부).
  // sessionStorage 에 저장 — 첫 방문 URL 에만 utm 이 붙고 이후 내부 링크 이동 시에는 사라지는 케이스를 메꿈.
  //
  // FP-1152: utm 과 광고 클릭 ID 는 동의 카테고리가 달라 저장소를 분리한다.
  //   utm 5종        → analytics (캠페인 파라미터, 개인 식별성 없음)     → gb_attr
  //   클릭 ID 3종     → marketing (광고 플랫폼이 개인에 연결하는 식별자) → gb_attr_click
  // "분석만 동의"한 이용자에게 클릭 ID 를 걷으면 카테고리 구분 자체가 무의미해진다.
  var ATTRIBUTION_GROUPS = [
    {
      category: CONSENT_ANALYTICS,
      storageKey: 'gb_attr',
      keys: ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'],
    },
    {
      category: CONSENT_MARKETING,
      storageKey: 'gb_attr_click',
      keys: ['ttclid', 'fbclid', 'gclid'],
    },
  ];
  var _attributionCache = {};   // storageKey → 캡처 결과

  function captureAttributionGroup(group) {
    if (!consentAllows(group.category)) return null;
    if (_attributionCache[group.storageKey]) return _attributionCache[group.storageKey];

    var stored = {};
    try {
      var raw = sessionStorage.getItem(group.storageKey);
      if (raw) stored = JSON.parse(raw) || {};
    } catch (e) { stored = {}; }

    // URL 에 새 값이 있으면 갱신 (광고 click 마다 latest-touch 로 덮어쓰기).
    var changed = false;
    try {
      var qs = new URLSearchParams(location.search);
      group.keys.forEach(function (k) {
        var v = qs.get(k);
        if (v) { stored[k] = v; changed = true; }
      });
    } catch (e) {
      // URLSearchParams 미지원 → 기존 저장값만 사용
      logError('tracking:captureAttribution:parseQuery', e);
    }

    if (changed) {
      try {
        sessionStorage.setItem(group.storageKey, JSON.stringify(stored));
      } catch (e) {
        // 저장 실패해도 이번 페이지 세션 동안은 메모리 캐시로 동작
        logError('tracking:captureAttribution:persist', e);
      }
    }
    _attributionCache[group.storageKey] = stored;
    return stored;
  }

  function captureAttribution() {
    var merged = {};
    ATTRIBUTION_GROUPS.forEach(function (group) {
      var captured = captureAttributionGroup(group);
      if (!captured) return;
      Object.keys(captured).forEach(function (k) { merged[k] = captured[k]; });
    });
    return merged;
  }

  function sessionId() {
    // 동의 전/거부: 세션 식별자를 만들지도 저장하지도 않는다.
    if (!consentAllows(CONSENT_ANALYTICS)) return null;
    try {
      var now = Date.now();
      var last = parseInt(sessionStorage.getItem(SESSION_TS_KEY) || '0', 10);
      if (isNaN(last)) last = 0;
      var existing = sessionStorage.getItem(SESSION_KEY);
      if (!existing || now - last > SESSION_IDLE_MS) {
        existing = uuid();
        sessionStorage.setItem(SESSION_KEY, existing);
      }
      sessionStorage.setItem(SESSION_TS_KEY, String(now));
      return existing;
    } catch (e) {
      return null;
    }
  }

  function deviceId() {
    // 동의 전/거부: 기기 식별자를 만들지도 저장하지도 않는다 (FP-1152 작업 6).
    if (!consentAllows(CONSENT_ANALYTICS)) return null;
    try {
      var existing = localStorage.getItem(DEVICE_KEY);
      if (!existing) {
        existing = uuid();
        localStorage.setItem(DEVICE_KEY, existing);
      }
      return existing;
    } catch (e) {
      return null;
    }
  }

  function deviceType() {
    var ua = (navigator && navigator.userAgent) || '';
    if (/iPad|Android(?!.*Mobile)|Tablet|PlayBook|Silk/i.test(ua)) return 'tablet';
    if (/Mobile|iPhone|iPod|Android|BlackBerry|IEMobile|Opera Mini/i.test(ua)) return 'mobile';
    return 'desktop';
  }

  function buildContext() {
    var liq = cfg.liquid_context || {};
    var ctx = {
      page_path: location.pathname,
      page_url: location.href,
      page_title: document.title,
      location: liq.location || null,
      locale: liq.locale || null,
      currency: liq.currency || null,
      template: liq.template || null,
      device_type: deviceType(),
      customer_id: liq.customer_id || null,
      customer_logged_in: !!liq.customer_logged_in,
      session_id: sessionId(),
      device_id: deviceId(),
    };
    var attr = captureAttribution();
    Object.keys(attr).forEach(function (k) { ctx[k] = attr[k]; });
    // A/B 테스트 참여 그룹 `abt` (snippets/abt-runtime.liquid · docs/AB-TEST.md) — 노출과 무관하게 모든 이벤트에
    try {
      var abt = window.GBABT && typeof window.GBABT.context === 'function' ? window.GBABT.context() : {};
      Object.keys(abt).forEach(function (k) { ctx[k] = abt[k]; });
    } catch (e) { /* 실험 런타임 오류가 트래킹을 막지 않게 */ }
    return ctx;
  }

  function eachAdapter(cb) {
    Object.keys(adapters).forEach(function (name) {
      cb(adapters[name], name);
    });
  }

  function selectAdapters(opts) {
    var only = opts && opts.only && opts.only.length ? opts.only : null;
    var exclude = opts && opts.exclude && opts.exclude.length ? opts.exclude : null;
    var selected = [];
    eachAdapter(function (a, name) {
      if (disabledSet[name]) return;
      if (only && only.indexOf(name) === -1) return;
      if (exclude && exclude.indexOf(name) !== -1) return;
      selected.push(a);
    });
    return selected;
  }

  function dispatchToAdapter(adapter, event, props, context, overrides) {
    try {
      if (typeof adapter.isAllowed === 'function' && !adapter.isAllowed(event)) return;
      var payload = adapter.transform(event, props, context);
      if (overrides) Object.assign(payload, overrides);

      // IS_DEV  : 로그만 찍고 전송 안 함 (localhost 기본)
      // IS_DEBUG: 로그도 찍고 전송도 함 (프로덕션 트러블슈팅)
      var logOnly = IS_DEV;
      if (IS_DEV || IS_DEBUG) {
        console.debug('[GBTrack:' + adapter.name + ']', event, payload);
      }
      if (logOnly) return;

      if (typeof adapter.isReady === 'function' && !adapter.isReady()) {
        adapter._queue = adapter._queue || [];
        adapter._queue.push(payload);
        return;
      }
      adapter.send(payload);
    } catch (err) {
      console.warn('[GBTrack:' + adapter.name + '] dispatch failed', err);
    }
  }

  // 동의 미확정/거부 구간 이벤트 보관소. 메모리 전용 — 스토리지에 절대 쓰지 않는다.
  var PENDING_MAX = 100;
  var pendingEvents = [];
  var pendingOverflowWarned = false;

  function flushPendingEvents() {
    if (!pendingEvents.length) return;
    // 어댑터는 defer 스크립트라 이 시점에 레지스트리가 비어 있을 수 있다.
    // globalQueueFlushed(=DOMContentLoaded 이후)를 기다렸다 flush 해야 이벤트가 유실되지 않는다.
    if (!globalQueueFlushed) return;
    var q = pendingEvents;
    pendingEvents = [];
    q.forEach(function (e) {
      try { track(e.event, e.props, e.options); }
      catch (err) { console.warn('[GBTrack] 동의 후 재전송 실패', err); }
    });
  }

  function track(event, props, options) {
    props = props || {};
    options = options || {};

    if (cfg.design_mode) return;  // 테마 에디터에서는 no-op
    if (!cfg.enabled) return;      // 전역 킬스위치 — IS_DEV gate는 dispatchToAdapter 안

    // analytics 동의 전에는 컨텍스트조차 만들지 않는다 (buildContext 가 식별자를 생성하므로).
    if (!consentAllows(CONSENT_ANALYTICS)) {
      // 확정된 거부 상태의 활동은 **폐기**한다. 버퍼링은 동의 여부가 아직 확정되지 않은
      // 구간(배너 미응답)에만 허용된다 — 거부 중 활동을 모아뒀다 나중에 동의하면 소급
      // 전송되는데, 그건 이용자가 동의한 범위가 아니다 (명세서 검증 시나리오 5).
      if (consentResolved()) return;
      if (pendingEvents.length < PENDING_MAX) {
        pendingEvents.push({ event: event, props: props, options: options });
      } else if (!pendingOverflowWarned) {
        pendingOverflowWarned = true;
        console.warn('[GBTrack] 동의 대기 큐 한도(' + PENDING_MAX + ') 초과 — 이후 이벤트는 폐기');
      }
      return;
    }

    var context = buildContext();
    var selected = selectAdapters(options);
    var overrides = options.overrides || {};

    selected.forEach(function (a) {
      dispatchToAdapter(a, event, props, context, overrides[a.name]);
    });

    recordHistory({
      t: Date.now(),
      event: event,
      props: props,
      context: context,
      adapters: selected.map(function (a) { return a.name; }),
    });
  }

  // 로그인 전환: user_id 설정. setUserId 는 세션 무해·멱등이라 로그인 로드마다 호출해도 안전.
  function setUser(id) {
    var uid = normalizeUserId(id);
    if (uid == null) return; // 해제는 resetUser 담당
    currentUserId = uid;
    eachAdapter(function (a) {
      if (typeof a.isReady === 'function' && !a.isReady()) return; // 미ready → ready 시 replay
      applyIdentityTo(a);
    });
  }

  // 로그아웃/계정전환: user_id 해제 + (필요 시) device 재발급.
  // 부트스트랩 인라인이 이미 재발급했으면(window.__gbDidReset) device 는 건너뜀.
  function resetUser() {
    currentUserId = null;
    /** @type {string | null} */
    var newDid = null;
    if (!window.__gbDidReset) {
      try {
        newDid = uuid();
        localStorage.setItem(DEVICE_KEY, newDid);
        window.__gbDidReset = true;
      } catch (e) { newDid = null; } // localStorage 차단 → device 미변경 degrade
    }
    eachAdapter(function (a) {
      // 미ready 어댑터는 init 시점에 (인라인이 갱신한) gb_did 로 익명 시작하므로 개입 불필요.
      if (typeof a.isReady === 'function' && !a.isReady()) return;
      if (newDid && typeof a.setDeviceId === 'function') {
        try { a.setDeviceId(newDid); }
        catch (err) { console.warn('[GBTrack:' + a.name + '] setDeviceId failed', err); }
      }
      if (typeof a.setUserId === 'function') {
        try { a.setUserId(null); appliedUid[a.name] = null; }
        catch (err) { console.warn('[GBTrack:' + a.name + '] setUserId(null) failed', err); }
      }
    });
  }

  // ── FP-1152 작업 7 · 철회 / 재동의 ────────────────────────────────────────
  // 철회: 전송 즉시 중단 + 대기 큐 폐기 + 어댑터 정리(Amplitude optOut·reset 등) + 로컬 식별자 삭제.
  // 재동의: 삭제된 식별자가 없으므로 다음 deviceId()/sessionId() 호출 때 신규 발급된다.
  // gb_abt · gb_abt_exposed: A/B 테스트 배정 기록 · 세션 노출 표시 (snippets/abt-runtime.liquid, docs/AB-TEST.md)
  var ANALYTICS_LOCAL_KEYS = [DEVICE_KEY, 'gb_last_uid', 'gb_login_pending', 'gb_signup_checked', 'gb_abt'];
  var ANALYTICS_SESSION_KEYS = [SESSION_KEY, SESSION_TS_KEY, 'gb_attr', 'gb_abt_exposed'];
  var MARKETING_SESSION_KEYS = ['gb_attr_click'];
  // 개인화(Personnalisation) 저장물. 최근 본 상품은 이용자가 요청한 서비스에 불가피한 저장이
  // 아니라 개인화라 preferences 동의 대상이다 (docs/CONSENT.md §4).
  var PREFERENCES_LOCAL_KEYS = ['viewedProducts'];

  function removeKeys(store, keys) {
    try { keys.forEach(function (k) { store.removeItem(k); }); }
    catch (e) {
      // 스토리지 차단 → 삭제 실패. 식별자가 남을 수 있으므로 기록은 남긴다.
      logError('tracking:removeKeys', e);
    }
  }

  function notifyAdapters(hook, arg) {
    eachAdapter(function (a) {
      if (typeof a[hook] !== 'function') return;
      try { a[hook](arg); }
      catch (err) { console.warn('[GBTrack:' + a.name + '] ' + hook + ' failed', err); }
    });
  }

  function onConsentChange(next, prev) {
    // 카테고리가 바뀌면 URL 재해석이 필요하다 (marketing 신규 동의 → 클릭 ID 캡처 등).
    _attributionCache = {};

    var revoked = {
      analytics: !!(prev && prev.analytics && !next.analytics),
      marketing: !!(prev && prev.marketing && !next.marketing),
    };
    var restored = {
      analytics: !!(prev && !prev.analytics && next.analytics),
      marketing: !!(prev && !prev.marketing && next.marketing),
    };

    // 거부로 확정된 순간 대기 큐를 비운다 (최초 확정이 거부인 경우 포함).
    if (!next.analytics) pendingEvents = [];

    if (revoked.analytics || revoked.marketing) {
      notifyAdapters('onConsentRevoked', revoked);
      if (revoked.analytics) {
        removeKeys(localStorage, ANALYTICS_LOCAL_KEYS);
        removeKeys(sessionStorage, ANALYTICS_SESSION_KEYS);
        currentUserId = null;
        appliedUid = {};
        window.__gbDidReset = false;
      }
      if (revoked.marketing) removeKeys(sessionStorage, MARKETING_SESSION_KEYS);
    }

    // preferences 는 전이가 아니라 현재 상태로 판단한다. onChange 는 동의가 확정된 뒤에만
    // 호출되므로(consent-signal 의 resolved 게이트) 미응답 구간에 지우는 일은 없다.
    // 전이만 보면 게이팅 전에 쌓인 데이터와 처음부터 거부한 이용자의 잔여물이 남는다.
    if (!next.preferences) {
      removeKeys(localStorage, PREFERENCES_LOCAL_KEYS);
    }

    if (restored.analytics || restored.marketing) {
      notifyAdapters('onConsentRestored', restored);
    }

    if (next.analytics) flushPendingEvents();
  }

  function register(adapter) {
    if (!adapter || !adapter.name) {
      console.warn('[GBTrack] register: adapter.name required');
      return;
    }
    adapters[adapter.name] = adapter;

    var adapterCfg = (cfg.adapters || {})[adapter.name] || {};
    try {
      if (typeof adapter.init === 'function') adapter.init(adapterCfg);
    } catch (err) {
      console.warn('[GBTrack:' + adapter.name + '] init failed', err);
      disabledSet[adapter.name] = true;
      return;
    }

    function drain() {
      var q = adapter._queue || [];
      adapter._queue = [];
      q.forEach(function (p) {
        try { adapter.send(p); } catch (err) {
          console.warn('[GBTrack:' + adapter.name + '] send failed', err);
        }
      });
      applyIdentityTo(adapter); // ready 된 어댑터에 현재 user_id replay (로드 순서 레이스 방어)
    }
    if (typeof adapter.onReady === 'function') {
      adapter.onReady(drain);
    } else if (typeof adapter.isReady === 'function') {
      var pollCount = 0;
      var POLL_MAX = 600; // 100ms × 600 = 60초
      var poll = setInterval(function () {
        pollCount++;
        if (adapter.isReady()) {
          clearInterval(poll);
          drain();
        } else if (pollCount >= POLL_MAX) {
          clearInterval(poll);
          console.warn('[GBTrack:' + adapter.name + '] isReady() 60초 내 true 반환 안 됨. polling 중단.');
        }
      }, 100);
    }
  }

  function flushGlobalQueue() {
    if (globalQueueFlushed) return;
    globalQueueFlushed = true;
    var q = window.__gbq || [];
    window.__gbq = [];
    q.forEach(function (args) {
      try {
        track.apply(null, args);
      } catch (err) {
        console.warn('[GBTrack] pre-core queue item failed', err);
      }
    });
    // 이후 들어오는 push는 즉시 track() 으로 전환.
    // 반드시 push([event, props, opts]) 형태로 호출해야 함 (인자 하나당 args 배열 하나).
    // push('event', props) 처럼 직접 인자를 전달하면 오동작함.
    window.__gbq = {
      push: function () {
        for (var i = 0; i < arguments.length; i++) {
          track.apply(null, arguments[i]);
        }
      }
    };

    // 어댑터 등록이 끝난 시점 → 동의 대기 큐도 여기서 처리 (동의가 이미 있었던 재방문자 케이스).
    flushPendingEvents();
  }

  window.GBTrack = {
    track: track,
    register: register,
    enable: function (name) { delete disabledSet[name]; },
    disable: function (name) { disabledSet[name] = true; },
    getContext: buildContext,
    history: function () { return _historyBuffer.slice(); },
    errors: function () { return _errorBuffer.slice(); },
    logError: logError,
    isDebug: function () { return IS_DEBUG; },
    setUser: setUser,
    resetUser: resetUser,
    isConsented: function () { return consentAllows(CONSENT_ANALYTICS); },
    _adapters: adapters,
  };

  // 동의 상태 구독. 최초 확정(prev=null) 시엔 정리 대상이 없으므로 대기 큐 flush 만 일어난다.
  if (window.GBConsent && typeof window.GBConsent.onChange === 'function') {
    window.GBConsent.onChange(onConsentChange);
  } else {
    console.warn('[GBTrack] GBConsent 미로드 — 전 이벤트 보류(fail-closed)');
  }

  // 어댑터들이 defer 순서로 모두 평가된 후에 queue drain.
  // 주의: tracking.js 자체가 defer 라 실행 시점엔 readyState === 'interactive' 인데,
  // 이 시점엔 뒤따르는 어댑터 defer 스크립트들이 아직 실행 안 됐다 (adapters 레지스트리 비어있음).
  // 즉시 flush 하면 selectAdapters() 가 [] 반환하여 __gbq 큐 이벤트가 어떤 어댑터에도 라우팅 안 되고 silently drop.
  // DOMContentLoaded 는 모든 defer 스크립트 실행 완료 후 fire 되므로 그때 flush 해야 안전.
  if (document.readyState === 'complete') {
    flushGlobalQueue();
  } else {
    document.addEventListener('DOMContentLoaded', flushGlobalQueue);
  }
})();
